/**
 * Document-Task Extraction Mycelium Connector
 *
 * Parses Google Docs structure to automatically generate hierarchical tasks.
 * Extracts action items, TODOs, and document structure into task lists.
 *
 * Key Features:
 * - Parses document headings for task hierarchy
 * - Extracts checkbox lists as subtasks
 * - Detects TODO/FIXME/ACTION items
 * - Links tasks back to document sections
 * - Maintains document-task sync
 *
 * Flow Example:
 * Google Doc created with:
 * # Project Plan
 * ## Phase 1
 * - [ ] Setup infrastructure
 * - [ ] Deploy services
 *
 * → Connector parses structure
 * → Creates parent task: "Project Plan"
 * → Creates subtasks: "Phase 1"
 * → Creates child tasks: "Setup infrastructure", "Deploy services"
 *
 * @module mycelium-document-task-extraction
 * @since 2026-02-20
 */

import { myceliumEventBackbone, type MyceliumEvent } from "./mycelium-event-backbone.service";
import { getMyceliumTransport } from "./mycelium-transport-registry";
import { logger } from "../lib/logger";

// ============================================================================
// CONFIGURATION
// ============================================================================

const TODO_PATTERNS = [
  /\[ \]/g,          // Unchecked checkbox
  /TODO:/gi,         // TODO marker
  /FIXME:/gi,        // FIXME marker
  /ACTION:/gi,       // ACTION marker
  /\* \[ \]/g,       // Markdown unchecked
  /-\s*\[ \]/g,      // Hyphen unchecked
] as const;

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class DocumentTaskExtractionConnector {
  private initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) return;

    logger.info("📄 Document-Task Extraction connector initializing");

    myceliumEventBackbone.subscribe({
      name: "DocumentTaskExtractionConnector",
      domains: ["integration"],
      eventTypes: ["google_docs_created", "google_docs_updated"],
      handler: this.handleDocumentEvent.bind(this),
      priority: 6,
    });

    this.initialized = true;
    logger.info("✅ Document-Task Extraction connector ready");
  }

  private async handleDocumentEvent(event: MyceliumEvent): Promise<void> {
    const { type, userId, payload } = event;

    try {
      const documentId = payload.documentId as string || payload.document_id as string;
      const content = payload.content as string || '';

      if (!documentId || !content) return;

      logger.debug("📄 Processing document for task extraction", {
        documentId,
        contentLength: content.length,
      });

      // Extract tasks from content
      const tasks = this.extractTasksFromContent(content, documentId);

      if (tasks.length === 0) {
        logger.debug("No tasks found in document", { documentId });
        return;
      }

      logger.info("✅ Extracted tasks from document", {
        documentId,
        taskCount: tasks.length,
      });

      // Publish task extraction event
      await getMyceliumTransport().publish({
        domain: "task",
        type: "tasks_extracted_from_document",
        userId,
        payload: {
          documentId,
          tasks,
          sourceEventId: event.id,
        },
      });
    } catch (error) {
      logger.error("❌ DocumentTaskExtraction handler error", {
        eventType: type,
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  private extractTasksFromContent(
    content: string,
    documentId: string
  ): Array<{ title: string; level: number; documentId: string }> {
    const tasks: Array<{ title: string; level: number; documentId: string }> = [];
    const lines = content.split('\n');

    for (const line of lines) {
      // Check for TODO patterns
      const hasTodo = TODO_PATTERNS.some(pattern => pattern.test(line));

      if (hasTodo) {
        // Extract task title (remove markers)
        const title = line
          .replace(/\[.\]/g, '')
          .replace(/TODO:|FIXME:|ACTION:/gi, '')
          .replace(/^[#*\-\s]+/, '')
          .trim();

        if (title.length > 0) {
          // Determine hierarchy level from markdown headers
          const headerMatch = line.match(/^(#+)\s/);
          const level = headerMatch ? headerMatch[1].length : 0;

          tasks.push({
            title,
            level,
            documentId,
          });
        }
      }
    }

    return tasks;
  }

  async dispose(): Promise<void> {
    this.initialized = false;
  }
}

export const documentTaskExtractionConnector = new DocumentTaskExtractionConnector();
